import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api/axios';

export default function RegisterPage() {
  const { token }           = useParams();
  const navigate            = useNavigate();
  const [form, setForm]     = useState({ name: '', phone: '', email: '', company_name: '', password: '', confirm: '' });
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    if (!form.name || !form.phone || !form.email || !form.company_name || !form.password)
      return 'All fields are required';
    if (!/^\d{10}$/.test(form.phone))
      return 'Phone must be exactly 10 digits';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return 'Enter a valid email address';
    if (form.password.length < 6)
      return 'Password must be at least 6 characters';
    if (form.password !== form.confirm)
      return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) return setError(err);
    setError(''); setLoading(true);
    try {
      const endpoint = token ? `/auth/register/${token}` : '/auth/register';
      await api.post(endpoint, {
        name: form.name, phone: form.phone,
        email: form.email, company_name: form.company_name, password: form.password
      });
      setSuccess('Account created! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. The link may be invalid or expired.');
    } finally { setLoading(false); }
  };

  const fields = [
    { name: 'name',         label: 'Full Name *',        placeholder: 'John Doe',         type: 'text',     maxLength: 100 },
    { name: 'phone',        label: 'Phone Number *',     placeholder: '10-digit number',  type: 'tel',      maxLength: 10  },
    { name: 'email',        label: 'Email Address *',    placeholder: 'john@company.com', type: 'email',    maxLength: 255 },
    { name: 'company_name', label: 'Company Name *',     placeholder: 'Acme Corp',        type: 'text',     maxLength: 255 },
  ];

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Brand */}
        <div style={s.brand}>
          <span style={{ fontSize: 38 }}>✉️</span>
          <div>
            <h1 style={s.brandName}>EmailVerifier</h1>
            <p style={s.brandSub}>msdiscoveries.com</p>
          </div>
        </div>

        <h2 style={s.title}>Create Your Account</h2>
        {token
          ? <p style={s.tokenBadge}>✅ Valid invite link detected</p>
          : <p style={s.sub}>Register using your admin-provided invite link</p>
        }

        {error   && <div style={s.errorBox}>⚠️ {error}</div>}
        {success && <div style={s.successBox}>✅ {success}</div>}

        <div style={s.formGrid}>
          {fields.map(({ name, label, placeholder, type, maxLength }) => (
            <div key={name} style={s.fieldGroup}>
              <label style={s.label}>{label}</label>
              <input
                style={s.input} name={name} type={type}
                placeholder={placeholder} value={form[name]}
                onChange={handleChange} maxLength={maxLength}
              />
            </div>
          ))}
        </div>

        {/* Password row */}
        <div style={s.pwdRow}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Password *</label>
            <div style={s.pwdWrap}>
              <input
                style={{ ...s.input, paddingRight: 40 }}
                name="password" type={showPwd ? 'text' : 'password'}
                placeholder="Min 6 characters"
                value={form.password} onChange={handleChange}
              />
              <button style={s.eyeBtn} onClick={() => setShowPwd(p => !p)} tabIndex={-1}>
                {showPwd ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Confirm Password *</label>
            <input
              style={{ ...s.input, border: form.confirm && form.confirm !== form.password ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0' }}
              name="confirm" type="password"
              placeholder="Re-enter password"
              value={form.confirm} onChange={handleChange}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            {form.confirm && form.confirm !== form.password &&
              <p style={s.matchErr}>Passwords don't match</p>
            }
          </div>
        </div>

        <button
          style={{ ...s.submitBtn, opacity: loading ? 0.75 : 1 }}
          onClick={handleSubmit} disabled={loading}>
          {loading ? '⏳ Creating account...' : '✅ Create Account'}
        </button>

        <p style={s.loginHint}>
          Already have an account? <Link to="/login" style={s.loginLink}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

const s = {
  page:       { minHeight: '100vh', background: '#111111', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  card:       { background: '#ffffff', borderRadius: 20, padding: '36px 32px', width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  brand:      { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
  brandName:  { color: '#3cb74e', fontSize: 22, fontWeight: 800, margin: 0 },
  brandSub:   { color: '#64748b', fontSize: 11, margin: 0 },
  title:      { color: '#111111', fontSize: 20, fontWeight: 800, margin: '0 0 4px' },
  sub:        { color: '#64748b', fontSize: 13, margin: '0 0 20px' },
  tokenBadge: { background: '#f0fdf4', color: '#166534', border: '1.5px solid #86efac', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, margin: '0 0 18px', display: 'inline-block' },
  errorBox:   { background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  successBox: { background: '#f0fdf4', border: '1.5px solid #86efac', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  formGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  pwdRow:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  fieldGroup: { marginBottom: 14 },
  label:      { display: 'block', color: '#111111', fontSize: 12, fontWeight: 700, marginBottom: 5 },
  input:      { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#111111', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  pwdWrap:    { position: 'relative' },
  eyeBtn:     { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 },
  matchErr:   { color: '#ef4444', fontSize: 11, margin: '4px 0 0' },
  submitBtn:  { width: '100%', padding: '13px', background: '#3cb74e', color: '#ffffff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 6, letterSpacing: '.2px' },
  loginHint:  { color: '#64748b', fontSize: 13, textAlign: 'center', marginTop: 16 },
  loginLink:  { color: '#3cb74e', textDecoration: 'none', fontWeight: 600 },
};
